<!--
  ChangelogChangeList.vue
  
  Displays a categorized list of changes (added, changed, fixed) with
  appropriate icons and color theming. Renders nothing if items array is empty.
  
  @example
  <ChangelogChangeList 
    type="added" 
    :items="['New feature X', 'Added button Y']" 
  />
-->
<template>
  <div v-if="items.length > 0" class="space-y-2">
    <!-- Section Header with Icon -->
    <div :class="['flex items-center gap-2 text-sm font-bold', colorClasses.text]">
      <!-- Icon Container -->
      <div :class="['flex items-center justify-center w-6 h-6 rounded-md', colorClasses.iconBg]">
        <!-- Plus icon for Added -->
        <svg
          v-if="type === 'added'"
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>

        <!-- Arrow icon for Changed/Improved -->
        <svg
          v-else-if="type === 'changed'"
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 7l5 5m0 0l-5 5m5-5H6"
          />
        </svg>

        <!-- Warning icon for Fixed -->
        <svg
          v-else-if="type === 'fixed'"
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>

      <!-- Section Label -->
      <span>{{ label }}</span>
    </div>

    <!-- Items List -->
    <ul class="space-y-1.5 ml-8">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="flex items-start gap-2 text-sm text-charcoal-200"
      >
        <span :class="['font-bold mt-0.5 flex-shrink-0', colorClasses.bullet]">•</span>
        <span>{{ item }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * Change type determines styling and icon
 * - added: New features (green/emerald)
 * - changed: Improvements (blue)
 * - fixed: Bug fixes (orange)
 */
type ChangeType = 'added' | 'changed' | 'fixed'

/**
 * Props for the ChangelogChangeList component
 */
interface Props {
  /** Type of changes - determines color scheme and icon */
  type: ChangeType
  /** Array of change descriptions to display */
  items: string[]
}

const props = defineProps<Props>()

/**
 * Label text based on change type
 */
const label = computed(() => {
  switch (props.type) {
    case 'added':
      return 'New Features'
    case 'changed':
      return 'Improvements'
    case 'fixed':
      return 'Bug Fixes'
    default:
      return ''
  }
})

/**
 * Color classes based on change type
 * Returns Tailwind classes for text, icon background, and bullet colors
 */
const colorClasses = computed(() => {
  switch (props.type) {
    case 'added':
      return {
        text: 'text-emerald-600',
        iconBg: 'bg-emerald-100',
        bullet: 'text-emerald-500',
      }
    case 'changed':
      return {
        text: 'text-blue-600',
        iconBg: 'bg-blue-100',
        bullet: 'text-blue-500',
      }
    case 'fixed':
      return {
        text: 'text-orange-600',
        iconBg: 'bg-orange-100',
        bullet: 'text-orange-500',
      }
    default:
      return {
        text: 'text-charcoal-300',
        iconBg: 'bg-charcoal-700',
        bullet: 'text-charcoal-400',
      }
  }
})
</script>
